<!--Wird eingebunden-->
<template>
  <div class="app-activation-country-input">
    <div class="inputs-container">
      <!-- Nationality field -->
      <div class="input-field">
        <label class="text-base-medium" for="nationality">
          <slot name="nationalityLabel">Staatsangehörigkeit</slot>
          <span class="required" aria-hidden="true">*</span>
        </label>
        <select
          id="nationality"
          class="input"
          v-model="nationality"
          required
          @input="$v.nationality.$touch"
          :class="{ error: !isValid('nationality') }"
          @change="resetIdentCard()"
        >
          <option v-for="option in nationalityOptions" :key="option.value.iso3" :value="option.value">
            {{ option.text }}
          </option>
        </select>
      </div>

      <!-- Identity card field -->
      <div class="input-field">
        <p v-if="disablePassport" class="text-sm text-error">
          <slot name="noIdentificationPossibleTxt">
            Eine Identifikation ist mit Ihrer Staatsangehörigkeit leider nicht möglich
          </slot>
        </p>
        <label class="text-base-medium" for="identCard">
          <slot name="identCardLabel">Ausweistyp</slot>
          <span class="required" aria-hidden="true">*</span>
        </label>
        <select
          id="identCard"
          class="input"
          v-model="identCard"
          required
          :disabled="disablePassport"
          @input="$v.identCard.$touch"
          @change="resetIdent()"
          :class="{ error: !isValid('identCard') }"
        >
          <option v-for="option in identityCardOptions" :key="option.value.key" :value="option.value">
            {{ option.text }}
          </option>
        </select>
      </div>

      <!-- Identification type radio group -->
      <div class="radio-group radio-group--vertical">
        <label>
          <div class="input-wrapper">
            <input
              type="radio"
              id="videoident"
              v-model="identType_"
              name="identType"
              value="video"
              :disabled="disableVideoIdent"
              @input="$v.identType.$touch"
            />
            <span class="custom-radio"></span>
          </div>
          <div class="radio-label-content">
            <span class="text-base-medium">Identifikation über Video-Anruf</span>
            <span class="text-base label-secondary-text"
              >Identifiziere dich online ganz schnell und einfach mit deinem persönlichen Video-Assistenten.</span
            >
          </div>
        </label>

        <label>
          <div class="input-wrapper">
            <input
              type="radio"
              id="postident"
              v-model="identType_"
              name="identType"
              value="store"
              :disabled="disablePostalIdentification"
              @input="$v.identType.$touch"
            />
            <span class="custom-radio"></span>
          </div>
          <div class="radio-label-content">
            <span class="text-base-medium">Identifikation über Postfiliale</span>
            <span class="text-base label-secondary-text"
              >Identifiziere dich ganz einfach persönlich in deiner Wunsch-Postfiliale</span
            >
          </div>
        </label>
      </div>

      <div class="checkbox-group">
        <label class="checkbox-class" for="dataMatchIdent" tabindex="0">
          <div class="input-wrapper">
            <input type="checkbox" id="dataMatchIdent" v-model="dataMatchIdent_" required @input="checkFormValidation" />
            <span class="custom-checkbox"></span>
          </div>
          <span class="text-base label-secondary-text"
            ><span class="required" aria-hidden="true">*</span>Meine angegebenen Daten stimmen mit denen meines Ausweisdokuments
            überein.</span
          >
        </label>
      </div>
      <p v-show="errorMessage" class="text-error">Bitte bestätige diese Feld</p>
    </div>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, sameAs } from 'vuelidate/lib/validators'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import isValid from '@/mixins/isValid'
import syncProperty from '@/mixins/syncProperty'
import preFilledInput from '@/mixins/preFilledInput'

export default {
  name: 'InputCountryData',
  data() {
    return {
      allIdentMethods: {
        identTypes: []
      }
    }
  },
  computed: {
    nationalityOptions: function() {
      return this.allIdentMethods.identTypes.map(identType => {
        return {
          value: identType,
          text: identType.name
        }
      })
    },
    identityCardOptions: function() {
      const identityCardOptions = this.nationality?.passports || []
      const filteredOptions = identityCardOptions
        .map(passport => {
          return {
            value: passport,
            text: passport.name
          }
        })
        .filter(passport => {
          return passport.value.identMethods.includes('store') || passport.value.identMethods.includes('video')
        })
      if (filteredOptions.length === 0) {
        return [this.disabledPassportObject].map(passport => {
          return {
            value: passport,
            text: passport.name
          }
        })
      } else {
        return filteredOptions
      }
    },
    currentIdents: function() {
      return this.identCard?.identMethods || []
    },
    disablePassport: function() {
      if (!this.nationality) {
        return false
      }
      return (
        this.identityCardOptions.filter(passportOption => {
          const passport = passportOption.value
          return passport.identMethods.includes('store') || passport.identMethods.includes('video')
        }).length === 0
      )
    },
    disableVideoIdent: function() {
      if (this.disablePassport) {
        return true
      }
      return this.currentIdents.includes('video') === false
    },
    disablePostalIdentification: function() {
      if (this.disablePassport) {
        return true
      }
      return this.currentIdents.includes('store') === false
    },
    defaultNationality: function() {
      return this.nationalityOptions.find(nationality => {
        return nationality.value.iso3 === this.defaultNationalityKey
      })?.value
    },
    defaultIdentCard: function() {
      return this.identityCardOptions.find(identCard => {
        return identCard.value.key === this.defaultIdentCardKey
      })?.value
    },
    disabledPassportObject: function() {
      return {
        __typename: 'NoPassport',
        name: '---',
        key: 'N/A',
        identMethods: []
      }
    }
  },
  watch: {
    '$v.$invalid': function(newValue) {
      this.$emit('update:invalid', newValue)
    },
    nationality: function(newValue) {
      this.nationality = newValue
    },
    identCard: function(newValue) {
      this.identCard = newValue
    }
  },
  methods: {
    checkFormValidation() {
      this.$v.$touch()
      this.$emit('validation')
      return this.$v.dataMatchIdent.$touch
    },
    resetIdentCard() {
      this.identCard = this.identityCardOptions[0].value
      this.resetIdent()
    },
    resetIdent() {
      if (this.identType_ !== null) {
        if (
          (this.identType_ === 'store' && this.disablePostalIdentification) ||
          (this.identType_ === 'video' && this.disableVideoIdent)
        ) {
          this.identType_ = null
        }
      } else {
        if (this.disableVideoIdent && this.disablePostalIdentification) {
          this.identType_ = null
        } else if (this.disableVideoIdent) {
          this.identType_ = 'store'
        } else if (this.disablePostalIdentification) {
          this.identType_ = 'video'
        } else {
          this.identType_ = 'video'
        }
      }
    }
  },
  mixins: [
    syncProperty('identType', 'identType_'),
    syncProperty('dataMatchIdent', 'dataMatchIdent_'),
    preFilledInput('nationality', 'defaultNationality', 'nationality'),
    preFilledInput('identCard', 'defaultIdentCard', 'identCard'),
    validationMixin,
    isValid
  ],
  mounted() {
    this.$emit('update:invalid', this.$v.$invalid)
  },
  validations() {
    return {
      identCard: {
        required,
        validPassport: {
          validPassportRequired: (value, vm) => {
            if (vm.__typename === 'NoPassport') {
              return false
            }
            return true
          }
        }
      },
      nationality: {
        required
      },
      identType: {
        required
      },
      dataMatchIdent: {
        sameAs: sameAs(() => true)
      }
    }
  },
  props: {
    defaultNationalityKey: {
      type: String,
      default: 'DEU'
    },
    defaultIdentCardKey: {
      type: String,
      default: 'IDC'
    },
    operationName: {
      type: String,
      default: 'activation'
    },
    errorMessage: Boolean
  },
  apollo: {
    allIdentMethods: function() {
      return wrapQuery(
        gql`query ${this.operationName} {
        allIdentMethods {
          identTypes {
            iso2,
            iso3,
            name,
            passports {
              identMethods,
              key,
              name
            }
          }
        }
      }`,
        this,
        'allIdentMethods',
        {},
        () => {},
        { fetchPolicy: 'cache-first' }
      )
    }
  }
}
</script>
